<template>
  <header class="site-header" :class="{ 'is-scrolled': scrolled, 'is-open': menuOpen }">
    <div class="site-header__inner container">
      <!-- Logo / home -->
      <a
        href="#top"
        class="brand"
        aria-label="Advanced System House ホームへ"
        @click="onNavClick($event, '#top')"
      >
        <img
          src="/logo.svg"
          class="brand__logo"
          alt="ASH — Advanced System House"
          width="82"
          height="48"
        />
      </a>

      <!-- Desktop nav -->
      <nav class="nav" aria-label="グローバルナビゲーション">
        <ul class="nav__list">
          <li v-for="item in navItems" :key="item.hash">
            <a
              :href="item.hash"
              class="nav__link"
              :class="{ 'is-active': activeId === item.id, 'nav__link--cta': item.cta }"
              @click="onNavClick($event, item.hash)"
            >{{ item.label }}</a>
          </li>
        </ul>
      </nav>

      <!-- Hamburger -->
      <button
        ref="burger"
        class="burger"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        aria-controls="mobile-nav"
        :aria-label="menuOpen ? 'メニューを閉じる' : 'メニューを開く'"
        @click="toggleMenu"
      >
        <span class="burger__box" aria-hidden="true">
          <span class="burger__line"></span>
          <span class="burger__line"></span>
          <span class="burger__line"></span>
        </span>
      </button>
    </div>

    <!-- Mobile overlay nav -->
    <transition name="drawer">
      <nav
        v-show="menuOpen"
        id="mobile-nav"
        ref="drawer"
        class="mobile-nav"
        aria-label="モバイルナビゲーション"
      >
        <ul class="mobile-nav__list">
          <li
            v-for="(item, i) in navItems"
            :key="item.hash"
            class="mobile-nav__item"
            :style="{ '--i': i }"
          >
            <a
              :href="item.hash"
              class="mobile-nav__link"
              @click="onNavClick($event, item.hash)"
            >
              <span class="mobile-nav__idx">{{ String(i).padStart(2, '0') }}</span>
              {{ item.label }}
            </a>
          </li>
        </ul>
        <div class="mobile-nav__foot">
          <a href="mailto:contact@ash.qa">contact@ash.qa</a>
          <span>栃木・宇都宮</span>
        </div>
      </nav>
    </transition>
  </header>
</template>

<script>
export default {
  name: 'SiteHeader',
  data() {
    return {
      scrolled: false,
      menuOpen: false,
      activeId: 'top',
      navItems: [
        { label: 'Home', hash: '#top', id: 'top' },
        { label: 'About', hash: '#about', id: 'about' },
        { label: 'Service', hash: '#service', id: 'service' },
        { label: 'AI Development', hash: '#ai-development', id: 'ai-development' },
        { label: 'Works', hash: '#works', id: 'works' },
        { label: 'Publications', hash: '#publications', id: 'publications' },
        { label: 'Contact', hash: '#contact', id: 'contact', cta: true },
      ],
      ticking: false,
      observer: null,
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onResize)
    this.onScroll()
    // Sibling sections mount after the header, so wait a tick for their DOM.
    this.$nextTick(this.setupObserver)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('keydown', this.onKeydown)
    if (this.observer) this.observer.disconnect()
    document.body.classList.remove('no-scroll')
  },
  methods: {
    onScroll() {
      if (this.ticking) return
      this.ticking = true
      window.requestAnimationFrame(() => {
        this.scrolled = window.scrollY > 24
        if (window.scrollY < 120) this.activeId = 'top'
        this.ticking = false
      })
    },
    onResize() {
      if (window.innerWidth > 900 && this.menuOpen) this.closeMenu()
    },
    setupObserver() {
      if (!('IntersectionObserver' in window)) return
      const ids = ['about', 'service', 'ai-development', 'works', 'publications', 'contact']
      const els = ids
        .map((id) => document.getElementById(id))
        .filter(Boolean)
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.activeId = entry.target.id
          })
        },
        { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
      )
      els.forEach((el) => this.observer.observe(el))
    },
    toggleMenu() {
      this.menuOpen ? this.closeMenu() : this.openMenu()
    },
    openMenu() {
      this.menuOpen = true
      document.body.classList.add('no-scroll')
      document.addEventListener('keydown', this.onKeydown)
      // Move focus into the drawer once it is shown.
      this.$nextTick(() => {
        const focusables = this.getDrawerFocusables()
        if (focusables.length) focusables[0].focus({ preventScroll: true })
      })
    },
    closeMenu({ returnFocus = false } = {}) {
      this.menuOpen = false
      document.body.classList.remove('no-scroll')
      document.removeEventListener('keydown', this.onKeydown)
      if (returnFocus && this.$refs.burger) {
        this.$refs.burger.focus({ preventScroll: true })
      }
    },
    getDrawerFocusables() {
      const drawer = this.$refs.drawer
      if (!drawer) return []
      return Array.from(
        drawer.querySelectorAll('a[href], button:not([disabled])')
      ).filter((el) => el.offsetParent !== null || el.getClientRects().length)
    },
    onKeydown(event) {
      if (!this.menuOpen) return
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeMenu({ returnFocus: true })
        return
      }
      if (event.key === 'Tab') {
        // Simple focus trap: keep Tab / Shift+Tab cycling inside the drawer.
        const focusables = this.getDrawerFocusables()
        if (!focusables.length) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        const active = document.activeElement
        const inside = this.$refs.drawer && this.$refs.drawer.contains(active)
        if (event.shiftKey) {
          if (active === first || !inside) {
            event.preventDefault()
            last.focus({ preventScroll: true })
          }
        } else if (active === last || !inside) {
          event.preventDefault()
          first.focus({ preventScroll: true })
        }
      }
    },
    onNavClick(event, hash) {
      // Let native smooth scroll (with scroll-margin-top offset) handle it,
      // but guarantee behavior for #top and close the mobile menu.
      if (this.menuOpen) this.closeMenu()
      const id = hash.replace('#', '')
      const target = id === 'top' ? document.body : document.getElementById(id)
      if (target) {
        event.preventDefault()
        if (id === 'top') {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          this.activeId = 'top'
          history.replaceState(null, '', location.pathname + location.search)
        } else {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' })
          this.activeId = id
          history.replaceState(null, '', hash)
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.site-header {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 100;
  height: var(--header-h);
  transition: background 0.35s var(--ease), box-shadow 0.35s var(--ease),
    border-color 0.35s var(--ease);
  border-bottom: 1px solid transparent;
}

.site-header.is-scrolled {
  background: rgba(243, 242, 236, 0.82);
  backdrop-filter: saturate(150%) blur(14px);
  -webkit-backdrop-filter: saturate(150%) blur(14px);
  border-bottom-color: var(--line);
  box-shadow: 0 12px 30px -24px rgba(20, 22, 27, 0.55);
}

.site-header.is-open {
  background: var(--paper);
}

.site-header__inner {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

/* --- Brand --- */
.brand {
  display: inline-flex;
  align-items: center;
}

.brand__logo {
  display: block;
  height: 48px;
  width: auto;
  transition: opacity 0.3s var(--ease);
}

.brand:hover .brand__logo {
  opacity: 0.72;
}

/* --- Desktop nav --- */
.nav__list {
  display: flex;
  align-items: center;
  gap: 6px;
}

.nav__link {
  position: relative;
  display: inline-block;
  padding: 8px 14px;
  font-weight: 600;
  font-size: 0.92rem;
  letter-spacing: 0.01em;
  color: var(--ink-soft);
  transition: color 0.25s var(--ease);
}

.nav__link::after {
  content: "";
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 2px;
  height: 1.5px;
  background: var(--accent);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s var(--ease);
}

.nav__link:hover {
  color: var(--ink);
}

.nav__link:hover::after,
.nav__link.is-active::after {
  transform: scaleX(1);
}

.nav__link.is-active {
  color: var(--accent);
}

.nav__link--cta {
  margin-left: 8px;
  padding: 9px 18px;
  color: var(--ink);
  border: 1px solid var(--line);
  border-radius: 999px;
  transition: border-color 0.25s var(--ease), color 0.25s var(--ease),
    background 0.25s var(--ease);
}

.nav__link--cta::after {
  display: none;
}

.nav__link--cta:hover,
.nav__link--cta.is-active {
  color: #fff;
  background: var(--accent);
  border-color: var(--accent);
}

/* --- Hamburger --- */
.burger {
  display: none;
  width: 46px;
  height: 46px;
  padding: 0;
  background: transparent;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  align-items: center;
  justify-content: center;
}

.burger__box {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 20px;
}

.burger__line {
  height: 2px;
  width: 100%;
  background: var(--ink);
  border-radius: 2px;
  transition: transform 0.3s var(--ease), opacity 0.2s var(--ease);
}

.is-open .burger__line:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.is-open .burger__line:nth-child(2) {
  opacity: 0;
}

.is-open .burger__line:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* --- Mobile overlay --- */
.mobile-nav {
  position: fixed;
  inset: var(--header-h) 0 0 0;
  background: var(--paper);
  padding: clamp(24px, 7vw, 48px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
}

.mobile-nav__list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
}

.mobile-nav__link {
  display: flex;
  align-items: baseline;
  gap: 16px;
  padding: 16px 4px;
  font-family: var(--font-jp);
  font-weight: 700;
  font-size: clamp(1.7rem, 8vw, 2.4rem);
  color: var(--ink);
  border-bottom: 1px solid var(--line-soft);
}

.mobile-nav__idx {
  font-family: var(--font-mono);
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--accent);
}

@media (max-height: 780px) {
  .mobile-nav__link {
    font-size: clamp(1.3rem, 6vw, 1.8rem);
    padding: 11px 4px;
  }
}

.mobile-nav__foot {
  display: flex;
  justify-content: space-between;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  color: var(--muted);
  padding-top: 24px;
}

/* Drawer transition */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s var(--ease), transform 0.35s var(--ease);
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.drawer-enter-active .mobile-nav__item {
  animation: mobileItemIn 0.5s var(--ease) both;
  animation-delay: calc(var(--i) * 60ms + 80ms);
}

@keyframes mobileItemIn {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 900px) {
  .nav {
    display: none;
  }
  .burger {
    display: inline-flex;
  }
  .brand__logo {
    height: 40px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .drawer-enter-active .mobile-nav__item {
    animation: none;
  }
}
</style>
